import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ActionIcon, Button, Group, Tooltip } from "@mantine/core";
import { useDisclosure, useDocumentTitle } from "@mantine/hooks";
import { IconPlus, IconRefresh } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import { useShallow } from "zustand/react/shallow";

import { WithPatientNavbar } from "@layouts/WithPatientNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { TabsContent, TabsList, TabsRoot } from "@components/Tabs";

import { PAGE_TITLE, QP } from "@constants/index";

import { labQKeys } from "@hooks/queries/lab/queryKeys";
import { useAppStore } from "@hooks/store/useAppStore";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";

import { AddModal } from "./components/AddModal";
import { FastTab } from "./components/FastTab";
import { GeneralTab } from "./components/GeneralTab";
import { ImagingTab } from "./components/ImagingTab";
import { TreatmentPlanTab } from "./components/TreatmentPlanTab";
import { StyledLaboratory } from "./Laboratory.styled";


export const Laboratory = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("laboratory")} • ${PAGE_TITLE}`);
  const { patientId } = useParams();

  const [storeTabs, setStoreTabs] = useAppStore(
    useShallow(state => [state.tabs, state.setTabs])
  );
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useQueryParam(
    QP.TAB,
    withDefault(StringParam, storeTabs.labTab)
  );

  const [opened, { open, close }] = useDisclosure(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { buttonRef } = useRefreshAnimation({ isRefreshing });

  useEffect(() => {
    if (currentTab) setCurrentTab(currentTab, "replaceIn");
  }, []);

  return (
    <WithSidebar hasPatientNavbar>
      <WithPatientNavbar>
        <StyledLaboratory>
          <TabsRoot
            value={currentTab as string}
            onValueChange={val => {
              setCurrentTab(val, "replace");
              setStoreTabs({ labTab: val });
            }}>
            <div className="header">
              <Group gap={"md"}>
                <TabsList
                  comboboxItems={[
                    { label: t("general"), value: "general" },
                    { label: t("radiology"), value: "imaging" },
                    { label: t("treatment-plan"), value: "treatment-plan" },
                    {
                      label: t("fast-consultation-files"),
                      value: "fast",
                    },
                  ]}
                />
                <Tooltip
                  label={t("refresh-data")}
                  position="right-end"
                  fz={12}
                  events={{ focus: true, hover: true, touch: true }}>
                  <ActionIcon
                    ref={buttonRef}
                    variant={"default"}
                    size={"md"}
                    onClick={() => {
                      queryClient.invalidateQueries(
                        labQKeys.patient(patientId as string)
                      );
                    }}>
                    <IconRefresh
                      className={"refresh-icon"}
                      color="var(--mantine-color-black)"
                      size={16}
                    />
                  </ActionIcon>
                </Tooltip>
              </Group>

              <AddModal opened={opened} close={close} />
              <PermissionGate permission="labs.create"><Button leftSection={<IconPlus size={18} />} onClick={open}>
                {t("add-lab")}
              </Button></PermissionGate>
            </div>

            <TabsContent value="general">
              <GeneralTab setIsRefreshing={setIsRefreshing} />
            </TabsContent>
            <TabsContent value="imaging">
              <ImagingTab setIsRefreshing={setIsRefreshing} />
            </TabsContent>
            <TabsContent value="treatment-plan">
              <TreatmentPlanTab setIsRefreshing={setIsRefreshing} />
            </TabsContent>

            <TabsContent value="fast">
              <FastTab setIsRefreshing={setIsRefreshing} />
            </TabsContent>

            <TabsContent value="radiology"></TabsContent>
            <TabsContent value="treatment-plan"></TabsContent>
          </TabsRoot>
        </StyledLaboratory>
      </WithPatientNavbar>
    </WithSidebar>
  );
};
